import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Category {
  id: string;
  name: string;
  slug: string;
  icon: string;
  image_url?: string;
}

interface Material {
  id: string;
  category_id: string;
  name: string;
  price_multiplier: number;
  image_url: string | null;
}

interface RepairType {
  id: string;
  category_id: string;
  material_id: string | null;
  name: string;
  description: string;
  base_price: number;
  image_url: string | null;
}

export default function InteractiveTool() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [repairTypes, setRepairTypes] = useState<RepairType[]>([]);
  
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedMaterial, setSelectedMaterial] = useState<string>('');
  const [selectedRepair, setSelectedRepair] = useState<string>('');
  const [estimatedPrice, setEstimatedPrice] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${SUPABASE_URL}/functions/v1/get-calculator-data`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const data = await response.json();
      
      setCategories(data.categories || []);
      setMaterials(data.materials || []);
      setRepairTypes(data.repairTypes || []);
    } catch (error) {
      console.error('Erreur de chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedCategoryData = categories.find(c => c.id === selectedCategory);
  const needsMaterial = selectedCategory ? true : false;

  const handleCategoryChange = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (category && category.slug === 'double-vitrage') {
      navigate('/calculateur-vitrage');
      return;
    }

    setSelectedCategory(categoryId);
    setSelectedMaterial('');
    setSelectedRepair('');
    setEstimatedPrice(0);
    
    setTimeout(() => {
      const materialElement = document.getElementById('material-section');
      if (materialElement) {
        const elementRect = materialElement.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
        window.scrollTo({ top: middle, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleMaterialChange = (materialId: string) => {
    setSelectedMaterial(materialId);
    setSelectedRepair('');
    setEstimatedPrice(0);
    
    setTimeout(() => {
      const repairElement = document.getElementById('repair-section');
      if (repairElement) {
        const elementRect = repairElement.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
        window.scrollTo({ top: middle, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleRepairChange = (repairId: string) => {
    setSelectedRepair(repairId);
    calculatePrice(repairId);
    
    setTimeout(() => {
      const priceElement = document.getElementById('estimated-price');
      if (priceElement) {
        const elementRect = priceElement.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
        window.scrollTo({ top: middle, behavior: 'smooth' });
      }
    }, 100);
  };

  const calculatePrice = (repairId: string) => {
    const repair = repairTypes.find(r => r.id === repairId);
    if (!repair) return;

    let price = repair.base_price;

    if (repair.material_id && selectedMaterial) {
      const material = materials.find(m => m.id === selectedMaterial);
      if (material) {
        price = price * material.price_multiplier;
      }
    }

    setEstimatedPrice(price);
  };

  const filteredMaterials = materials.filter(m => m.category_id === selectedCategory);
  
  const filteredRepairs = repairTypes.filter(r => {
    if (r.category_id !== selectedCategory) return false;
    
    if (filteredMaterials.length > 0) {
      return r.material_id === selectedMaterial;
    }
    
    return r.material_id === null;
  });

  const priceHTVA = estimatedPrice;
  const tva = priceHTVA * 0.06;
  const priceTTC = priceHTVA + tva;

  if (loading) {
    return (
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500"></div>
            <p className="mt-3 text-gray-600 text-sm">Chargement...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="calculateur" className="py-12 bg-gradient-to-br from-teal-50 via-white to-blue-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-64 h-64 bg-teal-200 rounded-full opacity-20 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-200 rounded-full opacity-20 blur-3xl translate-x-1/2 translate-y-1/2"></div>
      
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-teal-500 text-white px-5 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
            <i className="ri-calculator-line text-lg"></i>
            <span>Outil Gratuit</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Calculateur de Prix Instantané
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Estimation immédiate et transparente pour votre réparation
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border-2 border-teal-100">
            <div className="mb-6">
              <label className="block text-base font-semibold text-gray-800 mb-3">
                1. Type de menuiserie
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`relative overflow-hidden rounded-xl border-2 transition-all whitespace-nowrap cursor-pointer ${
                      selectedCategory === category.id
                        ? 'border-teal-500 bg-teal-50 shadow-lg scale-105'
                        : 'border-gray-200 hover:border-teal-300 hover:shadow-md'
                    }`}
                  >
                    <div className="aspect-square relative">
                      <img
                        src={category.image_url}
                        alt={category.name}
                        className="w-full h-full object-cover"
                      />
                      {selectedCategory === category.id && (
                        <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-teal-500 rounded-full">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                      )}
                    </div>
                    <div className="p-2 md:p-3 bg-white">
                      <p className="text-xs md:text-sm font-medium text-gray-800 text-center leading-tight">
                        {category.name}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedCategory && (
              <div id="material-section" className="mb-6 animate-fadeIn">
                <label className="block text-base font-semibold text-gray-800 mb-3">
                  2. Matériau
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                  {filteredMaterials.map((material) => (
                    <button
                      key={material.id}
                      onClick={() => handleMaterialChange(material.id)}
                      className={`relative overflow-hidden rounded-xl border-2 transition-all whitespace-nowrap cursor-pointer ${
                        selectedMaterial === material.id
                          ? 'border-teal-500 bg-teal-50 shadow-lg scale-105'
                          : 'border-gray-200 hover:border-teal-300 hover:shadow-md'
                      }`}
                    >
                      <div className="aspect-square relative">
                        <img
                          src={material.image_url}
                          alt={material.name}
                          className="w-full h-full object-cover"
                        />
                        {selectedMaterial === material.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-teal-500 rounded-full">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                        )}
                      </div>
                      <div className="p-2 md:p-3 bg-white">
                        <p className="text-xs md:text-sm font-medium text-gray-800 text-center leading-tight">
                          {material.name}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedMaterial && (
              <div id="repair-section" className="mb-6 animate-fadeIn">
                <label className="block text-base font-semibold text-gray-800 mb-3">
                  3. Type de réparation
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                  {filteredRepairs.map((repair) => (
                    <button
                      key={repair.id}
                      onClick={() => handleRepairChange(repair.id)}
                      className={`relative overflow-hidden rounded-xl border-2 transition-all whitespace-nowrap cursor-pointer ${
                        selectedRepair === repair.id
                          ? 'border-teal-500 bg-teal-50 shadow-lg scale-105'
                          : 'border-gray-200 hover:border-teal-300 hover:shadow-md'
                      }`}
                    >
                      <div className="aspect-square relative">
                        <img
                          src={repair.image_url}
                          alt={repair.name}
                          className="w-full h-full object-cover"
                        />
                        {selectedRepair === repair.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-teal-500 rounded-full">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                        )}
                      </div>
                      <div className="p-2 md:p-3 bg-white">
                        <p className="text-xs md:text-sm font-medium text-gray-800 text-center leading-tight">
                          {repair.name}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {estimatedPrice > 0 && (
              <div 
                id="estimated-price"
                className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-4 md:p-5 border-2 border-teal-200 shadow-lg"
              >
                <div className="text-center mb-3">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">Prix Estimé</h3>
                  <p className="text-xs text-gray-600">(incluant main d'œuvre et déplacement)</p>
                </div>

                <div className="flex items-center justify-center gap-2 md:gap-3 mb-3">
                  <div className="bg-white rounded-lg px-3 py-2 text-center">
                    <p className="text-xs text-gray-600 mb-0.5">HTVA</p>
                    <p className="text-base md:text-lg font-bold text-gray-900">{estimatedPrice.toFixed(2)}€</p>
                  </div>
                  <div className="text-gray-400">+</div>
                  <div className="bg-white rounded-lg px-3 py-2 text-center">
                    <p className="text-xs text-gray-600 mb-0.5">TVA 6%</p>
                    <p className="text-base md:text-lg font-bold text-gray-900">{(estimatedPrice * 0.06).toFixed(2)}€</p>
                  </div>
                  <div className="text-gray-400">=</div>
                  <div className="bg-teal-500 rounded-lg px-3 py-2 text-center">
                    <p className="text-xs text-white mb-0.5">Total TTC</p>
                    <p className="text-base md:text-lg font-bold text-white">{(estimatedPrice * 1.06).toFixed(2)}€</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 md:gap-3 max-w-xl mx-auto">
                  <a
                    href="https://wa.me/32475585219"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer aspect-square"
                  >
                    <div className="absolute inset-0">
                      <img
                        src="https://public.readdy.ai/ai/img_res/1600655c6bb7c0d14b77b457c95ce50b.jpg"
                        alt="Diagnostic WhatsApp"
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="400"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-green-700/98 via-green-600/95 to-green-500/90 md:from-green-600/95 md:via-green-500/80 md:to-green-400/60 group-hover:from-green-700/95 transition-all"></div>
                    </div>

                    <div className="relative h-full flex flex-col justify-between p-2 md:p-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white rounded-full shadow-lg self-end">
                        <i className="ri-whatsapp-fill text-xl md:text-2xl text-green-500"></i>
                      </div>

                      <div className="text-white">
                        <h4 className="text-xs md:text-sm lg:text-base font-bold mb-0.5 leading-tight">PHOTO/VIDÉO<br className="md:hidden" /> WHATSAPP</h4>
                        <p className="text-xs opacity-100 font-medium">Validation immédiate</p>
                      </div>
                    </div>
                  </a>

                  <a
                    href="#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer aspect-square"
                  >
                    <div className="absolute inset-0">
                      <img
                        src="https://readdy.ai/api/search-image?query=professional%20window%20repair%20technician%20arriving%20at%20residential%20building%20entrance%20for%20on-site%20service%20visit%2C%20expert%20craftsman%20in%20clean%20uniform%20carrying%20professional%20toolbox%20and%20equipment%20walking%20towards%20modern%20apartment%20building%2C%20friendly%20reliable%20service%20worker%20ready%20for%20window%20repair%20appointment%2C%20bright%20daylight%20Brussels%20Belgium%20street%20scene%2C%20realistic%20professional%20service%20photography&width=400&height=400&seq=onsite-visit-technician-arrival-2025&orientation=squarish"
                        alt="Visite technique"
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="400"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-700/98 via-blue-600/95 to-blue-500/90 md:from-blue-600/95 md:via-blue-500/80 md:to-blue-400/60 group-hover:from-blue-700/95 transition-all"></div>
                    </div>

                    <div className="relative h-full flex flex-col justify-between p-2 md:p-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white rounded-full shadow-lg self-end">
                        <i className="ri-calendar-check-fill text-xl md:text-2xl text-blue-500"></i>
                      </div>

                      <div className="text-white">
                        <h4 className="text-xs md:text-sm lg:text-base font-bold mb-0.5 leading-tight">VISITE<br className="md:hidden" /> GRATUITE</h4>
                        <p className="text-xs opacity-100 font-medium">Expert sur place</p>
                      </div>
                    </div>
                  </a>
                </div>

                <p className="text-center text-xs text-gray-500 mt-3">
                  <i className="ri-information-line mr-1"></i>
                  Prix indicatif - Confirmation après diagnostic
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
